import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Card,
  Typography,
  Modal,
  Descriptions,
  Form,
  DatePicker,
  Checkbox,
  App,
} from 'antd';
import {
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  PlusCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { StandingOrder, StandingOrderStatus, StandingOrderType, GenerateTransactionsRequest } from '@mygate/common';
import { transactionQueueService } from '@/services/transactionQueue';
import { ColumnsType } from 'antd/es/table';
import { formatDateTime } from '@/utils/date';
import dayjs from 'dayjs';
import { UserDetailModal } from './UserDetailModal';

const { Text } = Typography;

export function StandingOrdersTab() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<{ status?: string; isPaused?: string; type?: string }>({});
  const [userIdSearch, setUserIdSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [generateModalData, setGenerateModalData] = useState<{ orderId: string; nextDueDate?: Date | string } | null>(
    null,
  );
  const [generateForm] = Form.useForm();

  // Fetch standing orders
  const {
    data: standingOrders = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['standing-orders', filters],
    queryFn: () => transactionQueueService.fetchStandingOrders(filters),
  });

  // Pause mutation
  const pauseMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      transactionQueueService.pauseStandingOrder(id, reason || 'Manual pause by staff'),
    onSuccess: () => {
      message.success('Standing order paused successfully');
      queryClient.invalidateQueries({ queryKey: ['standing-orders'] });
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to pause standing order');
    },
  });

  // Resume mutation
  const resumeMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      transactionQueueService.resumeStandingOrder(id, reason || 'Manual resume by staff'),
    onSuccess: () => {
      message.success('Standing order resumed successfully');
      queryClient.invalidateQueries({ queryKey: ['standing-orders'] });
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to resume standing order');
    },
  });

  // Filter standing orders based on partial user ID match
  const filteredOrders = useMemo(() => {
    if (!userIdSearch) return standingOrders;

    const searchLower = userIdSearch.toLowerCase();
    return standingOrders.filter(order => {
      // Match partial userId
      if (order.userId?.toLowerCase().includes(searchLower)) return true;

      // Match user name if populated
      if (order.user) {
        const fullName = `${order.user.firstName || ''} ${order.user.lastName || ''}`.toLowerCase();
        if (fullName.includes(searchLower)) return true;

        // Match email
        if (order.user.email?.toLowerCase().includes(searchLower)) return true;

        // Match phone number
        if (order.user.phoneNumber?.includes(userIdSearch)) return true;
      }

      // Match standing order ID itself
      if (order.id?.toLowerCase().includes(searchLower)) return true;

      return false;
    });
  }, [standingOrders, userIdSearch]);

  const handlePause = (order: StandingOrder) => {
    // Use a simpler approach - direct mutation call with a prompt for reason
    const reason = window.prompt('Enter reason for pausing (optional):', 'Manual pause by staff');

    if (reason !== null) {
      // User clicked OK (even with empty string)
      pauseMutation.mutate({ id: order.id, reason: reason || 'Manual pause by staff' });
    }
  };

  const handleResume = (order: StandingOrder) => {
    // Use a simpler approach - direct mutation call with a prompt for reason
    const reason = window.prompt('Enter reason for resuming (optional):', 'Manual resume by staff');

    if (reason !== null) {
      // User clicked OK (even with empty string)
      resumeMutation.mutate({ id: order.id, reason: reason || 'Manual resume by staff' });
    }
  };

  const getStatusColor = (status: StandingOrderStatus) => {
    switch (status) {
      case StandingOrderStatus.ACTIVE:
        return 'success';
      case StandingOrderStatus.PENDING:
        return 'warning';
      case StandingOrderStatus.PROCESSING:
        return 'processing';
      case StandingOrderStatus.CANCELLED:
        return 'error';
      case StandingOrderStatus.FAILED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'MONTHLY':
        return 'Monthly';
      case 'WEEKLY':
        return 'Weekly';
      case 'DAILY':
        return 'Daily';
      case 'QUARTERLY':
        return 'Quarterly';
      case 'YEARLY':
        return 'Yearly';
      default:
        return frequency;
    }
  };

  const columns: ColumnsType<StandingOrder> = [
    {
      title: 'Type',
      key: 'type',
      width: 100,
      render: (_, record) => {
        // `type` is optional in legacy records that predate the Aliaksei
        // refactor (backfill script defaults them to 'regular'). Render
        // missing values as Regular so the column is never empty.
        const type = record.type ?? StandingOrderType.REGULAR;
        const isSparplan = type === StandingOrderType.SPARPLAN;
        return <Tag color={isSparplan ? 'cyan' : 'blue'}>{isSparplan ? 'Sparplan' : 'Regular'}</Tag>;
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <Space wrap>
          <Tag color={getStatusColor(record.status)}>{record.status}</Tag>
          {record.isPaused && <Tag color="orange">PAUSED</Tag>}
        </Space>
      ),
    },
    {
      title: 'Kunde & Auftrag',
      key: 'user',
      width: 150,
      render: (_, record) => {
        const userName = record.user
          ? `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim() ||
            record.user.phoneNumber ||
            'Unknown'
          : 'Loading...';

        return (
          <div className="table-cell-stack">
            <Button
              type="link"
              onClick={() => {
                setSelectedUserId(record.userId);
                setUserModalVisible(true);
              }}
              style={{ padding: 0 }}
            >
              {userName}
            </Button>
            <Button
              type="link"
              className="table-cell-link table-cell-muted"
              onClick={() => setSelectedOrderId(record.id)}
            >
              Auftrag {record.id.slice(-8)}
            </Button>
          </div>
        );
      },
    },
    {
      title: 'Beneficiary',
      key: 'beneficiary',
      width: 240,
      render: (_, record) => (
        <div>
          <div>
            <strong>{record.recipient}</strong>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.iban}
          </Text>
          <div>
            <Text className="table-cell-muted">{record.purpose || '—'}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (amount: string) => `€${amount}`,
    },
    {
      title: 'Intervall & Beginn',
      key: 'schedule',
      width: 155,
      render: (_, record) => (
        <div className="table-cell-stack">
          <Text>{getFrequencyLabel(record.interval)}</Text>
          <Text className="table-cell-muted">Seit {dayjs(record.startDate).format('DD.MM.YYYY')}</Text>
        </div>
      ),
    },
    {
      title: 'Next Due Date',
      dataIndex: 'nextDueDate',
      key: 'nextDueDate',
      width: 140,
      render: (date: string | null, record) => {
        if (record.isPaused) {
          return <Text type="secondary">Paused</Text>;
        }
        if (record.status !== StandingOrderStatus.ACTIVE) {
          return <Text type="secondary">N/A</Text>;
        }
        if (!date) {
          return <Text type="secondary">Pending</Text>;
        }

        const dueDate = dayjs(date);
        const today = dayjs().startOf('day');
        const isOverdue = dueDate.isBefore(today);

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Text style={{ color: isOverdue ? '#ff4d4f' : undefined, fontWeight: isOverdue ? 'bold' : undefined }}>
              {dueDate.format('YYYY-MM-DD')}
            </Text>
            {isOverdue && <Tag color="error">OVERDUE</Tag>}
          </div>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => formatDateTime(date),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) => {
        if (record.status !== StandingOrderStatus.ACTIVE) {
          return null;
        }

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {!record.isPaused && (
              <Button
                size="small"
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  setGenerateModalData({
                    orderId: record.id,
                    nextDueDate: record.nextDueDate,
                  });
                }}
                block
              >
                Generate
              </Button>
            )}
            {record.isPaused ? (
              <Button
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleResume(record)}
                loading={resumeMutation.isPending}
                block
              >
                Resume
              </Button>
            ) : (
              <Button
                size="small"
                icon={<PauseCircleOutlined />}
                onClick={() => handlePause(record)}
                loading={pauseMutation.isPending}
                danger
                block
              >
                Pause
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Filters */}
        <Space wrap>
          <Select
            placeholder="Filter by status"
            style={{ width: 150 }}
            allowClear
            onChange={value => setFilters(prev => ({ ...prev, status: value }))}
            value={filters.status}
          >
            <Select.Option value={StandingOrderStatus.ACTIVE}>Active</Select.Option>
            <Select.Option value={StandingOrderStatus.PENDING}>Pending</Select.Option>
            <Select.Option value={StandingOrderStatus.PROCESSING}>Processing</Select.Option>
            <Select.Option value={StandingOrderStatus.FAILED}>Failed</Select.Option>
            <Select.Option value={StandingOrderStatus.CANCELLED}>Cancelled</Select.Option>
          </Select>

          <Select
            placeholder="Paused state"
            style={{ width: 150 }}
            allowClear
            onChange={value => setFilters(prev => ({ ...prev, isPaused: value }))}
            value={filters.isPaused}
          >
            <Select.Option value="true">Paused</Select.Option>
            <Select.Option value="false">Not Paused</Select.Option>
          </Select>

          {/* Type filter (Aliakseis Direktive) — discriminates between
              regular PayCenter SEPA orders and external-bank crypto sparpläne. */}
          <Select
            placeholder="Filter by type"
            style={{ width: 150 }}
            allowClear
            onChange={value => setFilters(prev => ({ ...prev, type: value }))}
            value={filters.type}
          >
            <Select.Option value={StandingOrderType.REGULAR}>Regular</Select.Option>
            <Select.Option value={StandingOrderType.SPARPLAN}>Sparplan</Select.Option>
          </Select>

          <Input
            placeholder="Search user (partial ID/name/email)"
            style={{ width: 250 }}
            value={userIdSearch}
            onChange={e => setUserIdSearch(e.target.value)}
          />

          <Button
            onClick={() => {
              setFilters({});
              setUserIdSearch('');
            }}
          >
            Clear Filters
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Space>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredOrders}
          rowKey="id"
          loading={isLoading}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: total => `Total ${total} standing orders`,
          }}
          tableLayout="fixed"
          scroll={{ x: 1280 }}
        />

        {/* Standing Order Details Modal */}
        <Modal
          title="Standing Order Details"
          open={!!selectedOrderId}
          onCancel={() => setSelectedOrderId(null)}
          footer={(() => {
            const order = filteredOrders.find(o => o.id === selectedOrderId);
            return [
              order?.status === StandingOrderStatus.ACTIVE && !order?.isPaused && (
                <Button
                  key="generate"
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() => {
                    setGenerateModalData({
                      orderId: order.id,
                      nextDueDate: order.nextDueDate,
                    });
                  }}
                >
                  Generate Transaction
                </Button>
              ),
              <Button key="close" onClick={() => setSelectedOrderId(null)}>
                Close
              </Button>,
            ].filter(Boolean);
          })()}
          width={700}
        >
          {selectedOrderId &&
            (() => {
              const order = filteredOrders.find(o => o.id === selectedOrderId);
              if (!order) return null;

              return (
                <Descriptions bordered column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}>
                  <Descriptions.Item label="ID" span="filled">
                    {order.id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(order.status)}>{order.status}</Tag>
                    {order.isPaused && <Tag color="orange">PAUSED</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="User">
                    {order.user
                      ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() || order.user.phoneNumber
                      : order.userId}
                  </Descriptions.Item>
                  <Descriptions.Item label="Recipient" span="filled">
                    {order.recipient}
                  </Descriptions.Item>
                  <Descriptions.Item label="IBAN">{order.iban}</Descriptions.Item>
                  <Descriptions.Item label="BIC">{order.bic}</Descriptions.Item>
                  <Descriptions.Item label="Bank">{order.bankname}</Descriptions.Item>
                  <Descriptions.Item label="Amount">€{order.amount}</Descriptions.Item>
                  <Descriptions.Item label="Purpose" span="filled">
                    {order.purpose}
                  </Descriptions.Item>
                  <Descriptions.Item label="Frequency">{getFrequencyLabel(order.interval)}</Descriptions.Item>
                  <Descriptions.Item label="Start Date">
                    {dayjs(order.startDate).format('YYYY-MM-DD')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Next Due Date">
                    {order.isPaused
                      ? 'Paused'
                      : order.nextDueDate
                        ? dayjs(order.nextDueDate).format('YYYY-MM-DD')
                        : 'Pending'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">{formatDateTime(order.createdAt)}</Descriptions.Item>
                  {order.pausedAt && (
                    <Descriptions.Item label="Paused At" span="filled">
                      {formatDateTime(order.pausedAt)}
                    </Descriptions.Item>
                  )}
                  {order.resumedAt && (
                    <Descriptions.Item label="Resumed At" span="filled">
                      {formatDateTime(order.resumedAt)}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              );
            })()}
        </Modal>

        {/* User Details Modal */}
        <UserDetailModal
          userId={selectedUserId}
          visible={userModalVisible}
          onClose={() => {
            setSelectedUserId(null);
            setUserModalVisible(false);
          }}
          onUserUpdated={() => refetch()}
        />

        {/* Generate Transaction Modal for Single Standing Order */}
        <Modal
          title="Generate Transaction"
          open={!!generateModalData}
          onCancel={() => {
            setGenerateModalData(null);
            generateForm.resetFields();
          }}
          footer={null}
          width={600}
        >
          {generateModalData && (
            <Form
              form={generateForm}
              layout="vertical"
              onFinish={async values => {
                try {
                  const request: GenerateTransactionsRequest = {
                    tillDate: values.date.format('YYYY-MM-DD'),
                    standingOrderIds: [generateModalData.orderId],
                    dryRun: values.dryRun || false,
                  };

                  const response = await transactionQueueService.generateTransactions(request);

                  if (response) {
                    if (response.generated > 0) {
                      message.success(`Successfully generated ${response.generated} transaction(s)`);
                      queryClient.invalidateQueries({ queryKey: ['standing-orders'] });
                      queryClient.invalidateQueries({ queryKey: ['transactions'] });
                    } else if (response.skipped > 0) {
                      message.warning(`Transaction already exists for this date (${response.skipped} skipped)`);
                    } else if (response.errors.length > 0) {
                      message.error(`Failed: ${response.errors[0].reason}`);
                    } else {
                      message.info('No transactions were generated');
                    }
                  }

                  setGenerateModalData(null);
                  generateForm.resetFields();
                } catch (error) {
                  message.error((error instanceof Error && error.message) || 'Failed to generate transaction');
                }
              }}
              initialValues={{
                date: generateModalData.nextDueDate ? dayjs(generateModalData.nextDueDate) : dayjs(),
                dryRun: false,
              }}
            >
              {generateModalData.nextDueDate && (
                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#e6f7ff', borderColor: '#91d5ff' }}>
                  <Space wrap>
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    <Text>
                      Recommended date: <strong>{dayjs(generateModalData.nextDueDate).format('YYYY-MM-DD')}</strong>
                      {dayjs(generateModalData.nextDueDate).isBefore(dayjs().startOf('day')) && (
                        <Tag color="error" style={{ marginLeft: 8 }}>
                          OVERDUE
                        </Tag>
                      )}
                    </Text>
                  </Space>
                </Card>
              )}

              <Form.Item
                name="date"
                label="Generation Date"
                rules={[{ required: true, message: 'Please select a date' }]}
                help="Manual generation allows selecting any valid date. For regular processing, use the recommended date above."
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  showToday
                  onChange={selectedDate => {
                    if (
                      generateModalData.nextDueDate &&
                      selectedDate &&
                      !selectedDate.isSame(dayjs(generateModalData.nextDueDate), 'day')
                    ) {
                      message.info(
                        'Selected date differs from next due date. Transaction will be created for the selected date.',
                        3,
                      );
                    }
                  }}
                />
              </Form.Item>

              <Form.Item name="dryRun" valuePropName="checked">
                <Checkbox>
                  Dry run (preview only)
                  <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                    Shows what would be generated without actually creating the transaction
                  </Text>
                </Checkbox>
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space wrap>
                  <Button
                    onClick={() => {
                      setGenerateModalData(null);
                      generateForm.resetFields();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" icon={<PlusCircleOutlined />}>
                    Generate Transaction
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </Modal>
      </Space>
    </Card>
  );
}
